import type { TerminMailDto } from "../../services/calendarTerminService";
import type { Email } from "../../services/graphMailService";
import { AUB } from "../chat/chatTheme";

/**
 * P10 — die reine Logik hinter „die verknüpften Mails beim Termin sehen".
 *
 * Zwei Aufgaben, beide bewusst ohne React: aus dem MailLink-SNAPSHOT eine
 * Anzeigezeile machen (Absender, Betreff, Datum) und daraus das `Email`-Objekt
 * bauen, mit dem `openMailDetailWindow` die Mail nachlädt. Der Snapshot ist die
 * einzige Quelle — der Termin soll seine Post ohne Graph-Abruf zeigen können,
 * auch wenn die Nachricht inzwischen verschoben oder gelöscht wurde.
 */

/** Anzeige-Zeile einer am Termin hängenden Mail. */
export type TerminMailZeile = {
    /** Stabiler React-Key (die Link-Id ist eindeutig). */
    key: string;
    linkId: number;
    mailbox: string;
    messageId: string;
    betreff: string;
    absender: string;
    datum: string;
};

/** Initialen für den Avatar-Buchstaben des Detailfensters. */
export function initialen(name?: string | null): string {
    return (name || "?")
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

/**
 * Datum eines Snapshots als „21.08.2026 · 14:30". Leer bei fehlendem oder
 * unbrauchbarem Wert — eine Epoch-0-Zeile („01.01.1970") wäre schlimmer als
 * gar keine Angabe.
 */
export function terminMailDatum(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()) || d.getTime() === 0) return "";
    return `${d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    })} · ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
}

/**
 * Absender-Anzeige: Name, sonst Adresse, sonst ein ehrliches „Unbekannt".
 * Beides kann fehlen — der Snapshot wurde beim Verknüpfen geschrieben, und
 * ältere Zeilen tragen nicht jedes Feld.
 */
export function terminMailAbsender(m: Pick<TerminMailDto, "senderName" | "senderEmail">): string {
    const name = (m.senderName ?? "").trim();
    if (name) return name;
    const adresse = (m.senderEmail ?? "").trim();
    return adresse || "Unbekannt";
}

/** Snapshot → Anzeigezeile. */
export function terminMailZeile(m: TerminMailDto): TerminMailZeile {
    return {
        key: String(m.id),
        linkId: m.id,
        mailbox: m.mailbox,
        messageId: m.messageId,
        betreff: (m.subject ?? "").trim() || "(Kein Betreff)",
        absender: terminMailAbsender(m),
        datum: terminMailDatum(m.receivedAt),
    };
}

/**
 * Snapshot → `Email` fürs Detailfenster. Body und Anhänge bleiben leer: das
 * Fenster lädt die Nachricht über `mailbox` + `id` (messageId) selbst nach —
 * genau wie im Schriftwechsel des Vorgangs und bei den Todo-Mails.
 */
export function emailAusTerminMail(m: TerminMailDto): Email {
    return {
        id: m.messageId,
        conversationId: m.conversationId ?? "",
        senderName: m.senderName ?? "Unbekannt",
        senderEmail: m.senderEmail ?? "",
        senderInitial: initialen(m.senderName ?? m.senderEmail),
        senderColor: AUB,
        subject: m.subject ?? "(Kein Betreff)",
        date: m.receivedAt ? new Date(m.receivedAt) : new Date(0),
        dateLabel: "",
        preview: m.preview ?? "",
        body: "",
        type: "normal",
        read: true,
        to: [],
        cc: [],
    };
}
